/**
 * DocsEditor — TipTap rich-text editor for documentation pages.
 * Routes: /documentation/new  and  /documentation/:id
 *
 * Layout:
 *   ┌─────────────── TopBar ────────────────────────────────┐
 *   │ TipTap Toolbar                                        │
 *   │ ┌─────────────────────────────┐ ┌──────────────────┐  │
 *   │ │      TipTap Canvas          │ │  Meta/Links panel │  │
 *   │ └─────────────────────────────┘ └──────────────────┘  │
 *   └──────────────────────────────────────────────────────-┘
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckIcon,
  BookmarkIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  LinkIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Placeholder from '@tiptap/extension-placeholder';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';

import api from '../services/api';
import '../styles/tiptap-editor.css';
import { autorLegible, fechaCorta, fechaHora } from '../utils/autores';

// ── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_OPTS = [
  { value: 'draft',     label: 'Borrador' },
  { value: 'published', label: 'Publicado' },
];

// ── Imágenes pegadas o arrastradas ───────────────────────────────────────────
//
// Pegar una imagen copiada de una web "funcionaba" por casualidad: llega como
// HTML con un <img> y lo inserta el manejador por defecto de TipTap. Una captura
// de pantalla, en cambio, llega como fichero `image/*` en `clipboardData.items`
// (en Linux siempre, y en Windows con Win+Shift+S) y el manejador por defecto la
// ignora: el usuario ve que "no pasa nada".
//
// Aquí se recoge esa imagen y se incrusta en el documento, reduciéndola antes:
// el HTML y el JSON del documento llevan la imagen dentro, y cada versión
// guardada se lleva otra copia, así que una captura de 4K tal cual dejaría el
// documento inservible.

const MAX_IMAGE_SIDE = 1600;        // px de lado mayor: sobra para leer una captura
const SMALL_ENOUGH_BYTES = 220 * 1024;
const WEBP_QUALITY = 0.9;

function hasFileItems(dataTransfer) {
  if (!dataTransfer) return false;
  if (dataTransfer.files && dataTransfer.files.length) return true;
  return Array.from(dataTransfer.items || []).some(item => item.kind === 'file');
}

function imageFilesFrom(dataTransfer) {
  if (!dataTransfer) return [];
  const files = [];
  // `items` es la fuente fiable: es donde el navegador deja el image/png del
  // portapapeles (Linux incluido).
  Array.from(dataTransfer.items || []).forEach(item => {
    if (item.kind !== 'file') return;
    if (!String(item.type || '').startsWith('image/')) return;
    const file = item.getAsFile();
    if (file) files.push(file);
  });
  // `files` como respaldo.
  if (!files.length) {
    Array.from(dataTransfer.files || []).forEach(file => {
      if (String(file.type || '').startsWith('image/')) files.push(file);
    });
  }
  return files;
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('No se pudo leer la imagen'));
    reader.readAsDataURL(file);
  });
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const el = document.createElement('img');
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('La imagen del portapapeles no se pudo interpretar'));
    el.src = src;
  });
}

async function shrinkImageFile(file) {
  const original = await fileToDataUrl(file);
  if (!original.startsWith('data:image/')) {
    throw new Error('El portapapeles no traía una imagen');
  }
  // Los vectores no se reescalan: se quedarían pixelados.
  if (original.startsWith('data:image/svg')) return original;

  const el = await loadImageElement(original);
  const w = el.naturalWidth || 0;
  const h = el.naturalHeight || 0;
  if (!w || !h) return original;

  const scale = Math.min(1, MAX_IMAGE_SIDE / Math.max(w, h));
  if (scale === 1 && file.size <= SMALL_ENOUGH_BYTES) return original;

  const width = Math.max(1, Math.round(w * scale));
  const height = Math.max(1, Math.round(h * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return original;
  ctx.drawImage(el, 0, 0, width, height);

  // WebP conserva la transparencia y pesa menos; si el navegador no lo soporta,
  // toDataURL devuelve PNG y lo aceptamos igual.
  const webp = canvas.toDataURL('image/webp', WEBP_QUALITY);
  const out = webp.startsWith('data:image/webp') ? webp : canvas.toDataURL('image/png');
  return out.length < original.length ? out : original;
}

// ── Toolbar Component ────────────────────────────────────────────────────────

function TipTapToolbar({ editor }) {
  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes('link').href;
    const url = prompt('URL del enlace:', prev || 'https://');
    if (url === null) return;
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const addImage = () => {
    const url = prompt('URL de la imagen:');
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  const Btn = ({ onClick, active, disabled, title, children }) => (
    <button
      type="button"
      onClick={onClick}
      className={active ? 'is-active' : ''}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );

  return (
    <div className="tiptap-toolbar">
      {/* Heading dropdown */}
      <div className="tiptap-toolbar-group">
        <select
          value={
            editor.isActive('heading', { level: 1 }) ? '1' :
            editor.isActive('heading', { level: 2 }) ? '2' :
            editor.isActive('heading', { level: 3 }) ? '3' : '0'
          }
          onChange={e => {
            const v = parseInt(e.target.value);
            if (v === 0) editor.chain().focus().setParagraph().run();
            else editor.chain().focus().toggleHeading({ level: v }).run();
          }}
          title="Tipo de bloque"
        >
          <option value="0">Párrafo</option>
          <option value="1">Título H1</option>
          <option value="2">Subtítulo H2</option>
          <option value="3">Encabezado H3</option>
        </select>
      </div>

      {/* Basic formatting */}
      <div className="tiptap-toolbar-group">
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Negrita">
          <b>B</b>
        </Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Cursiva">
          <i>I</i>
        </Btn>
        <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Subrayado">
          <u>U</u>
        </Btn>
        <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Tachado">
          <s>S</s>
        </Btn>
        <Btn onClick={() => editor.chain().focus().toggleHighlight().run()} active={editor.isActive('highlight')} title="Resaltar">
          🖍
        </Btn>
        <Btn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Código inline">
          {'</>'}
        </Btn>
      </div>

      {/* Alignment */}
      <div className="tiptap-toolbar-group">
        <Btn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Alinear izquierda">
          ≡
        </Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Centrar">
          ≡
        </Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Alinear derecha">
          ≡
        </Btn>
      </div>

      {/* Lists */}
      <div className="tiptap-toolbar-group">
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Lista">
          •
        </Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Lista numerada">
          1.
        </Btn>
        <Btn onClick={() => editor.chain().focus().toggleTaskList().run()} active={editor.isActive('taskList')} title="Lista de tareas">
          ☑
        </Btn>
      </div>

      {/* Block elements */}
      <div className="tiptap-toolbar-group">
        <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Cita">
          ❝
        </Btn>
        <Btn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Bloque de código">
          {'{ }'}
        </Btn>
        <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Separador">
          ─
        </Btn>
      </div>

      {/* Table */}
      <div className="tiptap-toolbar-group">
        <Btn onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insertar tabla">
          ⊞
        </Btn>
        {editor.isActive('table') && (
          <>
            <Btn onClick={() => editor.chain().focus().addColumnAfter().run()} title="Añadir columna">+C</Btn>
            <Btn onClick={() => editor.chain().focus().addRowAfter().run()} title="Añadir fila">+R</Btn>
            <Btn onClick={() => editor.chain().focus().deleteTable().run()} title="Eliminar tabla" style={{ color: '#dc2626' }}>✕T</Btn>
          </>
        )}
      </div>

      {/* Links & Media */}
      <div className="tiptap-toolbar-group">
        <Btn onClick={setLink} active={editor.isActive('link')} title="Enlace">
          🔗
        </Btn>
        <Btn onClick={addImage} title="Imagen por URL">
          🖼
        </Btn>
      </div>

      {/* Undo/Redo */}
      <div className="tiptap-toolbar-group">
        <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Deshacer">
          ↩
        </Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Rehacer">
          ↪
        </Btn>
      </div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function DocsEditor({ companyId }) {
  const navigate = useNavigate();
  const { id }   = useParams();
  const [searchParams] = useSearchParams();
  const isNew    = !id;

  // Auto-link: when coming from SmartButton (?linkType=server&linkId=42&linkName=SRV01)
  const autoLinkType = searchParams.get('linkType');
  const returnPath   = searchParams.get('returnPath');
  const autoLinkId   = searchParams.get('linkId');
  const autoLinkName = searchParams.get('linkName');
  const autoLinkPending = useRef(!!(autoLinkType && autoLinkId));

  // Page meta state
  const [title,       setTitle]       = useState('');
  const [description, setDescription] = useState('');
  const [status,      setStatus]      = useState('draft');
  const [tags,        setTags]        = useState([]);
  const [tagInput,    setTagInput]    = useState('');
  const [docId,       setDocId]       = useState(isNew ? null : parseInt(id));

  // Links
  const [links,         setLinks]         = useState([]);
  const [linkSearch,    setLinkSearch]    = useState('');
  const [linkResults,   setLinkResults]   = useState([]);
  const [linkSearching, setLinkSearching] = useState(false);

  // URLs
  const [urls,     setUrls]     = useState([]);
  const [newUrl,   setNewUrl]   = useState({ title: '', url: '', description: '' });
  const [addingUrl, setAddingUrl] = useState(false);

  // Versions
  const [versions,    setVersions]    = useState([]);
  const [changeNote,  setChangeNote]  = useState('');

  // Quién y cuándo (created_by / updated_by / fechas del documento)
  const [traza, setTraza] = useState({ created_by: null, updated_by: null, created_at: null, updated_at: null, latest_version: 0 });

  // UI state
  const [rightTab, setRightTab]     = useState(searchParams.get('tab') === 'versions' ? 'versions' : 'meta');
  const [saving,   setSaving]       = useState(false);
  const [error,    setError]        = useState('');
  const [loading,  setLoading]      = useState(!isNew);
  const [showRight, setShowRight]   = useState(true);

  // Content loaded marker (to set editor content once)
  const contentLoaded = useRef(false);

  // ── Imágenes pegadas/arrastradas ──────────────────────────────────────────
  const editorRef = useRef(null);
  const [hint, setHint] = useState('');

  useEffect(() => {
    if (!hint) return undefined;
    const timer = setTimeout(() => setHint(''), 12000);
    return () => clearTimeout(timer);
  }, [hint]);

  const insertImages = useCallback(async (files) => {
    const list = Array.from(files || []);
    for (const file of list) {
      // eslint-disable-next-line no-await-in-loop
      try {
        const src = await shrinkImageFile(file);
        editorRef.current
          ?.chain()
          .focus()
          .setImage({ src, alt: file.name || 'Imagen pegada' })
          .run();
      } catch (err) {
        setError(err.message || 'No se pudo insertar la imagen');
      }
    }
  }, []);

  const handleMediaPaste = useCallback((event) => {
    const files = imageFilesFrom(event.clipboardData);
    if (files.length) {
      event.preventDefault();
      insertImages(files);
      return true;
    }
    // Copiar un fichero desde el explorador sólo deja su RUTA en el portapapeles,
    // y el navegador no puede leerla por seguridad. Mejor decirlo que dejar al
    // usuario creyendo que "no funciona".
    const types = Array.from(event.clipboardData?.types || []);
    if (types.includes('text/uri-list')) {
      setHint('Si has copiado un archivo desde el explorador de tu equipo, arrástralo aquí o usa el botón de imagen: el navegador no permite leerlo al pegar.');
    }
    return false;
  }, [insertImages]);

  const handleMediaDrop = useCallback((event) => {
    // Ojo: los arrastres INTERNOS (mover un texto o un bloque dentro del propio
    // documento) no traen ficheros y deben seguir funcionando como siempre.
    if (!hasFileItems(event.dataTransfer)) return false;
    // Se corta SIEMPRE el comportamiento por defecto: sin esto el navegador abre
    // el fichero soltado y te saca del editor con el documento sin guardar.
    event.preventDefault();
    const files = imageFilesFrom(event.dataTransfer);
    if (files.length) {
      insertImages(files);
    } else {
      setHint('Aquí sólo se insertan imágenes. Para adjuntar documentos, usa «Subir documento» en la biblioteca.');
    }
    return true;
  }, [insertImages]);

  // ── TipTap editor ──────────────────────────────────────────────────────────

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight.configure({ multicolor: false }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } }),
      Image.configure({ inline: false, allowBase64: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Placeholder.configure({ placeholder: 'Escribe aquí o arrastra contenido…' }),
      TextStyle,
      Color,
    ],
    content: '<p></p>',
    editorProps: {
      attributes: {
        spellcheck: 'true',
      },
      handlePaste: (_view, event) => handleMediaPaste(event),
      handleDrop: (_view, event) => handleMediaDrop(event),
    },
  });

  // El editor se crea una sola vez y sus `editorProps` no se refrescan, así que
  // los manejadores de arriba trabajan contra esta referencia.
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // ── Load existing doc ──────────────────────────────────────────────────────

  const loadDoc = useCallback(async () => {
    if (!id || !companyId) return;
    setLoading(true);
    try {
      const [doc, lnkList, urlList, verList] = await Promise.all([
        api.getDocPage(id, companyId),
        api.getDocPageLinks(id, companyId),
        api.getDocPageUrls(id, companyId),
        api.listDocPageVersions(id, companyId),
      ]);
      setTitle(doc.title || '');
      setDescription(doc.description || '');
      setStatus(doc.status || 'draft');
      setTags(doc.tags || []);
      setLinks(lnkList || []);
      setUrls(urlList || []);
      setVersions(verList || []);
      setTraza({
        created_by: doc.created_by_name || doc.created_by || null,
        updated_by: doc.updated_by_name || doc.updated_by || null,
        created_at: doc.created_at || null,
        updated_at: doc.updated_at || null,
        latest_version: doc.latest_version || 0,
      });

      // Set editor content from TipTap JSON or HTML
      if (editor && !contentLoaded.current) {
        if (doc.content_json) {
          try {
            const json = JSON.parse(doc.content_json);
            editor.commands.setContent(json);
            contentLoaded.current = true;
          } catch (_) {
            if (doc.content_html) {
              editor.commands.setContent(doc.content_html);
              contentLoaded.current = true;
            }
          }
        } else if (doc.content_html) {
          editor.commands.setContent(doc.content_html);
          contentLoaded.current = true;
        }
      }
    } catch (err) {
      setError(err.message || 'Error cargando documento');
    } finally { setLoading(false); }
  }, [id, companyId, editor]);

  useEffect(() => {
    if (!isNew && editor) {
      const t = setTimeout(() => loadDoc(), 100);
      return () => clearTimeout(t);
    }
  }, [isNew, loadDoc, editor]);

  // ── Save ──────────────────────────────────────────────────────────────────

  const getEditorContent = () => {
    if (!editor) return { content_json: null, content_html: '' };
    return {
      content_json: JSON.stringify(editor.getJSON()),
      content_html: editor.getHTML(),
    };
  };

  const handleSave = async (publish = false) => {
    if (!title.trim()) { setError('El título es obligatorio'); return; }
    setError(''); setSaving(true);
    const { content_json, content_html } = getEditorContent();
    const payload = {
      title: title.trim(), description: description.trim() || null,
      status: publish ? 'published' : status, tags,
      doc_type: 'richtext',
      content_json, content_html,
    };
    try {
      if (isNew || !docId) {
        const created = await api.createDocPage(companyId, payload);
        setDocId(created.id);
        window.history.replaceState(null, '', `/documentation/${created.id}`);
        setStatus(created.status);
        aplicarTraza(created);
        // Auto-link if coming from a SmartButton
        if (autoLinkPending.current && autoLinkType && autoLinkId) {
          try {
            await api.addDocPageLink(created.id, companyId, autoLinkType, parseInt(autoLinkId), autoLinkName || null);
            autoLinkPending.current = false;
            // Refresh links panel
            const updatedLinks = await api.getDocPageLinks(created.id, companyId);
            setLinks(updatedLinks);
          } catch (_) { /* link failed, non-blocking */ }
        }
      } else {
        const updated = await api.updateDocPage(docId, companyId, payload);
        setStatus(updated.status);
        aplicarTraza(updated);
      }
    } catch (err) { setError(err.message || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  /** Refresca «quién y cuándo» con lo que devuelve el backend al guardar. */
  const aplicarTraza = (doc) => {
    if (!doc) return;
    setTraza(t => ({
      created_by: doc.created_by_name ?? doc.created_by ?? t.created_by,
      updated_by: doc.updated_by_name ?? doc.updated_by ?? t.updated_by,
      created_at: doc.created_at ?? t.created_at,
      updated_at: doc.updated_at ?? t.updated_at,
      latest_version: doc.latest_version ?? t.latest_version,
    }));
  };

  const handleSaveVersion = async () => {
    if (!docId) { alert('Guarda el documento primero'); return; }
    const { content_json, content_html } = getEditorContent();
    try {
      const res = await api.saveDocPageVersion(docId, companyId, {
        content_json, content_html, change_note: changeNote || null,
      });
      setVersions(prev => [res.data, ...prev]);
      setChangeNote('');
      // El documento acaba de cambiar: refresca quién y cuándo.
      aplicarTraza(res.doc);
      alert(`Versión ${res.data.version} guardada`);
    } catch (err) { alert(err.message || 'No se pudo guardar la versión'); }
  };

  const handleRestoreVersion = async (ver) => {
    if (!window.confirm(`¿Restaurar versión ${ver.version}?`)) return;
    try {
      const full = await api.getDocPageVersion(docId, ver.version, companyId);
      if (editor) {
        if (full.content_json) {
          try { editor.commands.setContent(JSON.parse(full.content_json)); }
          catch (_) { if (full.content_html) editor.commands.setContent(full.content_html); }
        } else if (full.content_html) {
          editor.commands.setContent(full.content_html);
        }
      }
    } catch (err) { alert(err.message || 'No se pudo restaurar'); }
  };

  // ── Links ─────────────────────────────────────────────────────────────────

  const doLinkSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setLinkResults([]); return; }
    setLinkSearching(true);
    try { setLinkResults(await api.searchElementsForDoc(q, companyId)); }
    catch (_) {} finally { setLinkSearching(false); }
  }, [companyId]);

  useEffect(() => {
    const t = setTimeout(() => doLinkSearch(linkSearch), 300);
    return () => clearTimeout(t);
  }, [linkSearch, doLinkSearch]);

  const addLink = async (el) => {
    if (!docId) { alert('Guarda el documento primero'); return; }
    try {
      const lnk = await api.addDocPageLink(docId, companyId, el.element_type, el.element_id, el.element_name);
      setLinks(prev => prev.some(l => l.id === lnk.id) ? prev : [...prev, lnk]);
      setLinkSearch(''); setLinkResults([]);
    } catch (err) { alert(err.message); }
  };

  const removeLink = async (lnk) => {
    if (!window.confirm(`¿Quitar "${lnk.element_name}"?`)) return;
    try { await api.removeDocPageLink(docId, lnk.id, companyId); setLinks(prev => prev.filter(l => l.id !== lnk.id)); }
    catch (err) { alert(err.message); }
  };

  // ── URLs ──────────────────────────────────────────────────────────────────

  const addUrl = async () => {
    if (!newUrl.title.trim() || !newUrl.url.trim()) { alert('Título y URL obligatorios'); return; }
    if (!docId) { alert('Guarda primero'); return; }
    try {
      const u = await api.addDocPageUrl(docId, companyId, newUrl);
      setUrls(prev => [...prev, u]);
      setNewUrl({ title: '', url: '', description: '' }); setAddingUrl(false);
    } catch (err) { alert(err.message); }
  };

  const removeUrl = async (url) => {
    if (!window.confirm(`¿Quitar "${url.title}"?`)) return;
    try { await api.removeDocPageUrl(docId, url.id, companyId); setUrls(prev => prev.filter(u => u.id !== url.id)); }
    catch (err) { alert(err.message); }
  };

  // Tags
  const addTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/\s+/g, '-');
    if (!t || tags.includes(t)) { setTagInput(''); return; }
    setTags(prev => [...prev, t]); setTagInput('');
  };
  const removeTag = (t) => setTags(prev => prev.filter(x => x !== t));

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--text-secondary)' }}>
        Cargando documento…
      </div>
    );
  }

  return (
    <div className="docs-editor-page" style={{ display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden', background: 'var(--slate-50)' }}>

      {/* ── TopBar ── */}
      <div className="docs-editor-topbar" style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        padding: '0 1rem', height: 48, minHeight: 48,
        background: '#1e293b', color: '#f8fafc', flexShrink: 0,
        borderBottom: '1px solid #334155',
      }}>
        {autoLinkType && autoLinkId ? (
          <>
            <button
              onClick={() => navigate(returnPath || '/documentation', { state: { openId: autoLinkId } })}
              style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.82rem' }}
            >
              <ArrowLeftIcon style={{ width: 16, height: 16 }} /> {autoLinkName || 'Elemento'}
            </button>
            <span style={{ color: '#475569' }}>|</span>
            <button onClick={() => navigate('/documentation')} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.75rem' }}>
              Documentación
            </button>
          </>
        ) : (
          <button onClick={() => navigate('/documentation')} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.82rem' }}>
            <ArrowLeftIcon style={{ width: 16, height: 16 }} /> Documentación
          </button>
        )}
        <span style={{ color: '#475569' }}>|</span>
        <span style={{ fontSize: '0.82rem', color: '#cbd5e1' }}>
          {title || 'Nuevo documento'}
        </span>
        <div style={{ flex: 1 }} />
        {error && <span style={{ color: '#fca5a5', fontSize: '0.75rem', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>⚠ {error}</span>}
        <span style={{
          padding: '2px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 600,
          background: status === 'published' ? '#166534' : '#854d0e',
          color: status === 'published' ? '#bbf7d0' : '#fef08a',
        }}>
          {status === 'published' ? 'Publicado' : 'Borrador'}
        </span>
        <button onClick={() => handleSaveVersion()} disabled={saving || !docId} style={topBtnStyle} title="Guardar versión">
          <ClockIcon style={topIconStyle} />
        </button>
        <button onClick={() => handleSave(false)} disabled={saving} style={{ ...topBtnStyle, background: '#334155' }}>
          {saving ? '…' : <CheckIcon style={topIconStyle} />}
          <span style={{ fontSize: '0.78rem' }}>{saving ? 'Guardando' : 'Guardar'}</span>
        </button>
        {status !== 'published' && (
          <button onClick={() => handleSave(true)} disabled={saving} style={{ ...topBtnStyle, background: '#2563eb' }}>
            <BookmarkIcon style={topIconStyle} />
            <span style={{ fontSize: '0.78rem' }}>Publicar</span>
          </button>
        )}
        <button onClick={() => setShowRight(p => !p)} style={topBtnStyle} title="Panel derecho">
          {showRight ? <ChevronRightIcon style={topIconStyle} /> : <ChevronLeftIcon style={topIconStyle} />}
        </button>
      </div>

      {/* ── Body ── */}
      <div className="docs-editor-body" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ── CENTER: TipTap editor ── */}
        <div className="docs-editor-main" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div className="tiptap-editor-wrap" style={{ margin: 0, borderRadius: 0, border: 'none', flex: 1, display: 'flex', flexDirection: 'column' }}>
            <TipTapToolbar editor={editor} />
            {hint && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '0.5rem 0.75rem',
                  background: '#1e293b',
                  color: '#cbd5e1',
                  fontSize: '0.78rem',
                  borderBottom: '1px solid #334155',
                }}
              >
                <span style={{ flex: 1 }}>{hint}</span>
                <button
                  type="button"
                  onClick={() => setHint('')}
                  title="Cerrar aviso"
                  style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
                >
                  ✕
                </button>
              </div>
            )}
            <div style={{ flex: 1, overflow: 'auto' }}>
              <EditorContent editor={editor} style={{ height: '100%' }} />
            </div>
          </div>
        </div>

        {/* ── RIGHT: Meta panel ── */}
        <div className={`docs-editor-side${showRight ? ' is-open' : ''}`} style={{
          width: showRight ? 300 : 0,
          minWidth: showRight ? 300 : 0,
          background: 'var(--bg-surface)',
          borderLeft: showRight ? '1px solid var(--border)' : 'none',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          flexShrink: 0,
          transition: 'width 0.2s ease, min-width 0.2s ease',
        }}>
          {showRight && (
            <>
              {/* Tabs */}
              <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
                {[
                  { id: 'meta',     label: 'Meta' },
                  { id: 'links',    label: `Vínculos (${links.length})` },
                  { id: 'urls',     label: `URLs (${urls.length})` },
                  { id: 'versions', label: 'Versiones' },
                ].map(tab => (
                  <button key={tab.id} onClick={() => setRightTab(tab.id)}
                    style={{
                      flex: 1, padding: '0.5rem 0', border: 'none', background: 'transparent',
                      borderBottom: rightTab === tab.id ? '2px solid var(--accent)' : '2px solid transparent',
                      fontSize: '0.72rem', fontWeight: rightTab === tab.id ? 700 : 500,
                      color: rightTab === tab.id ? 'var(--accent-l)' : 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >{tab.label}</button>
                ))}
              </div>

              {/* Panel body */}
              <div style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>

                {/* META */}
                {rightTab === 'meta' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                    {/* Quién y cuándo: es lo primero que se pregunta al abrir la
                        documentación de un elemento que no has escrito tú. */}
                    {docId && (
                      <div className="doc-meta-traza">
                        <p>
                          {autorLegible(traza.created_by)
                            ? <>Creado por <strong>{autorLegible(traza.created_by)}</strong></>
                            : 'Documento del equipo'}
                          {fechaCorta(traza.created_at) && <> el {fechaCorta(traza.created_at)}</>}
                        </p>
                        {traza.updated_at && traza.updated_at !== traza.created_at && (
                          <p>
                            Última modificación
                            {autorLegible(traza.updated_by)
                              ? <> de <strong>{autorLegible(traza.updated_by)}</strong></>
                              : null}
                            {' '}el {fechaHora(traza.updated_at)}
                          </p>
                        )}
                        <p>
                          {traza.latest_version > 0
                            ? <>Guarda {traza.latest_version} {traza.latest_version === 1 ? 'versión' : 'versiones'} en el historial.</>
                            : 'Todavía no hay versiones guardadas.'}
                          {' '}
                          <button type="button" className="doc-link-btn" onClick={() => setRightTab('versions')}>
                            Ver el historial
                          </button>
                        </p>
                      </div>
                    )}
                    <Field label="Título *">
                      <input style={inputStyle} value={title} onChange={e => setTitle(e.target.value)} placeholder="Título del documento" />
                    </Field>
                    <Field label="Descripción">
                      <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }} value={description} onChange={e => setDescription(e.target.value)} placeholder="Descripción breve…" rows={3} />
                    </Field>
                    <Field label="Estado">
                      <select style={inputStyle} value={status} onChange={e => setStatus(e.target.value)}>
                        {STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Etiquetas">
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.4rem' }}>
                        {tags.map(t => (
                          <span key={t} style={tagStyle}>
                            {t}
                            <button onClick={() => removeTag(t)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#93c5fd', lineHeight: 1, fontSize: 14 }}>×</button>
                          </span>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <input style={{ ...inputStyle, flex: 1, fontSize: '0.8rem' }} placeholder="Nueva etiqueta…"
                          value={tagInput} onChange={e => setTagInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                        />
                        <button onClick={addTag} style={smallBtnStyle}><PlusIcon style={{ width: 14, height: 14 }} /></button>
                      </div>
                    </Field>
                  </div>
                )}

                {/* LINKS */}
                {rightTab === 'links' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <p style={hintStyle}>Vincula este documento con elementos de la infraestructura.</p>
                    {!docId && <div style={warningBox}>Guarda el documento primero.</div>}
                    {docId && (
                      <>
                        <div style={{ position: 'relative' }}>
                          <MagnifyingGlassIcon style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: 'var(--text-muted)' }} />
                          <input style={{ ...inputStyle, paddingLeft: 26 }} placeholder="Buscar servidor, rack, firewall…"
                            value={linkSearch} onChange={e => setLinkSearch(e.target.value)} />
                        </div>
                        {linkSearching && <p style={{ ...hintStyle, color: 'var(--text-muted)' }}>Buscando…</p>}
                        {linkResults.length > 0 && (
                          <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', maxHeight: 200, overflowY: 'auto' }}>
                            {linkResults.map((el, i) => (
                              <button key={i} onClick={() => addLink(el)} style={searchResultStyle}>
                                <div>
                                  <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{el.element_name}</span>
                                  {el.extra && <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>({el.extra})</span>}
                                </div>
                                <span style={badgeStyle}>{el.element_type}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    {links.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                        <p style={subheaderStyle}>Vínculos actuales</p>
                        {links.map(lnk => (
                          <div key={lnk.id} style={linkRowStyle}>
                            <div style={{ fontSize: '0.8rem' }}>
                              <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{lnk.element_name || lnk.element_id}</span><br />
                              <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>{lnk.element_type}</span>
                            </div>
                            <button onClick={() => removeLink(lnk)} style={delBtnStyle}><XMarkIcon style={{ width: 14, height: 14 }} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    {links.length === 0 && docId && <p style={emptyStyle}>Sin vínculos. Busca arriba.</p>}
                  </div>
                )}

                {/* URLS */}
                {rightTab === 'urls' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <p style={hintStyle}>URLs de referencia externas.</p>
                    {!docId && <div style={warningBox}>Guarda el documento primero.</div>}
                    {urls.map(u => (
                      <div key={u.id} style={{ background: 'var(--bg-toolbar)', border: '1px solid var(--border)', borderRadius: 6, padding: '0.6rem', display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                        <LinkIcon style={{ width: 14, height: 14, color: 'var(--accent-l)', flexShrink: 0, marginTop: 2 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <a href={u.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--accent-l)', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.title}</a>
                          {u.description && <p style={{ margin: '0.2rem 0 0', fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{u.description}</p>}
                        </div>
                        <button onClick={() => removeUrl(u)} style={delBtnStyle}><XMarkIcon style={{ width: 13, height: 13 }} /></button>
                      </div>
                    ))}
                    {docId && (addingUrl ? (
                      <div style={{ background: 'var(--bg-toolbar)', border: '1px solid var(--border)', borderRadius: 6, padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <input style={inputStyle} placeholder="Título *" value={newUrl.title} onChange={e => setNewUrl(p => ({ ...p, title: e.target.value }))} />
                        <input style={inputStyle} placeholder="https://…" value={newUrl.url} onChange={e => setNewUrl(p => ({ ...p, url: e.target.value }))} />
                        <input style={inputStyle} placeholder="Descripción (opcional)" value={newUrl.description} onChange={e => setNewUrl(p => ({ ...p, description: e.target.value }))} />
                        <div style={{ display: 'flex', gap: '0.4rem' }}>
                          <button style={{ ...smallBtnStyle, flex: 1, background: '#2563eb', color: '#fff' }} onClick={addUrl}>Añadir</button>
                          <button style={smallBtnStyle} onClick={() => setAddingUrl(false)}>Cancelar</button>
                        </div>
                      </div>
                    ) : (
                      <button style={{ ...smallBtnStyle, width: '100%', justifyContent: 'center' }} onClick={() => setAddingUrl(true)}>
                        <PlusIcon style={{ width: 14, height: 14 }} /> Añadir URL
                      </button>
                    ))}
                    {urls.length === 0 && !addingUrl && <p style={emptyStyle}>Sin URLs de referencia.</p>}
                  </div>
                )}

                {/* VERSIONS */}
                {rightTab === 'versions' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <p style={hintStyle}>
                      Cada versión guardada es una foto del contenido: quién la hizo, cuándo y qué cambió.
                    </p>
                    {docId && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <input style={inputStyle} placeholder="Nota de cambio (opcional)" value={changeNote} onChange={e => setChangeNote(e.target.value)} />
                        <button style={{ ...smallBtnStyle, width: '100%', justifyContent: 'center' }} onClick={handleSaveVersion}>
                          <ClockIcon style={{ width: 14, height: 14 }} /> Guardar versión
                        </button>
                      </div>
                    )}
                    {!docId && <div style={warningBox}>Guarda el documento primero.</div>}
                    {versions.length > 0 && (
                      <div className="doc-hist">
                        <p style={subheaderStyle}>Historial</p>
                        {versions.map(v => {
                          const autor = autorLegible(v.created_by_name || v.created_by);
                          const esActual = v.version === traza.latest_version;
                          return (
                            <div key={v.version} className="doc-hist-row">
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div className="doc-hist-head">
                                  <span className="doc-hist-ver">v{v.version}</span>
                                  {esActual && <span className="list-badge list-badge--on">Actual</span>}
                                  {v.change_note && (
                                    <span className="list-hint" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {v.change_note}
                                    </span>
                                  )}
                                </div>
                                <div className="doc-row-meta" style={{ margin: 0 }}>
                                  {autor && <span>Creada por <strong>{autor}</strong></span>}
                                  <span>{fechaHora(v.created_at)}</span>
                                </div>
                              </div>
                              <button
                                onClick={() => handleRestoreVersion(v)}
                                style={smallBtnStyle}
                                title="Traer esta versión al editor"
                              >
                                <DocumentDuplicateIcon style={{ width: 12, height: 12 }} /> Restaurar
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {versions.length === 0 && <p style={emptyStyle}>Sin versiones.</p>}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Tiny reusable helpers ───────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

const topBtnStyle = {
  display: 'flex', alignItems: 'center', gap: 4,
  background: 'transparent', border: '1px solid #475569', borderRadius: 5,
  color: '#e2e8f0', padding: '4px 10px', cursor: 'pointer', fontSize: '0.8rem',
};
const topIconStyle = { width: 16, height: 16 };

const inputStyle = {
  width: '100%', padding: '0.4rem 0.6rem',
  border: '1px solid var(--border)', borderRadius: 6,
  fontSize: '0.82rem', outline: 'none', fontFamily: 'inherit',
  background: 'var(--bg-input)', color: 'var(--text-primary)',
};
const smallBtnStyle = {
  display: 'flex', alignItems: 'center', gap: 4,
  padding: '0.35rem 0.65rem', border: '1px solid var(--border)', borderRadius: 6,
  background: 'var(--bg-toolbar)', cursor: 'pointer', fontSize: '0.78rem', color: 'var(--text-secondary)',
};
const tagStyle = {
  background: 'rgba(59,130,246,.16)', color: '#93c5fd', fontSize: '0.72rem', fontWeight: 600,
  padding: '0.15rem 0.4rem', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4,
};
const hintStyle      = { fontSize: '0.78rem', color: 'var(--text-secondary)', margin: 0 };
const emptyStyle     = { fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'center', padding: '1rem 0' };
const subheaderStyle = { fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 };
const warningBox     = { background: 'rgba(245,158,11,.15)', border: '1px solid rgba(245,158,11,.4)', borderRadius: 6, padding: '0.5rem 0.75rem', fontSize: '0.78rem', color: '#fcd34d' };
const searchResultStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  width: '100%', background: 'var(--bg-toolbar)', border: 'none',
  borderBottom: '1px solid var(--border)', padding: '0.4rem 0.65rem',
  cursor: 'pointer', fontSize: '0.8rem', textAlign: 'left', color: 'var(--text-primary)',
};
const badgeStyle   = { fontSize: '0.68rem', color: 'var(--text-secondary)', background: 'var(--bg-toolbar)', borderRadius: 3, padding: '0.1rem 0.35rem' };
const linkRowStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  background: 'var(--bg-toolbar)', border: '1px solid var(--border)', borderRadius: 6, padding: '0.4rem 0.6rem',
};
const delBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '0.2rem' };
